"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2, Play } from "lucide-react";
import { walkthroughVideoProps } from "./exclusive-playback";
import { SimpleTooltip } from "@/components/ui/tooltip";

/**
 * Click-to-play walkthrough video, poster first: the mp4 is only fetched once
 * someone asks for it, so the page paints without downloading a video.
 *
 * Videos are produced with the `walkthrough-video` skill
 * (.claude/skills/walkthrough-video) and live under public/guides/.
 *
 * Fullscreen is handled here rather than left to the browser's built-in
 * control: the figure (not the bare video) goes fullscreen, with a prefixed
 * fallback for iOS Safari, so the same button works in every browser.
 *
 * @param {{ src: string, poster: string, title: string, width?: number, height?: number, className?: string }} props
 *   `title` describes the walkthrough ("set up and use Salesably in ChatGPT")
 *   and feeds the poster alt text and the play button's accessible name.
 */
export default function WalkthroughVideo({
  src,
  poster,
  title,
  width = 1600,
  height = 966,
  className = "",
}) {
  const [playing, setPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const figureRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const sync = () => {
      const active =
        document.fullscreenElement || document.webkitFullscreenElement || null;
      setIsFullscreen(Boolean(active) && active === figureRef.current);
    };
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const figure = figureRef.current;
    const video = videoRef.current;
    if (!figure) return;
    try {
      const active =
        document.fullscreenElement || document.webkitFullscreenElement;
      if (active) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        return;
      }
      if (figure.requestFullscreen) {
        await figure.requestFullscreen();
      } else if (figure.webkitRequestFullscreen) {
        figure.webkitRequestFullscreen();
      } else if (video?.webkitEnterFullscreen) {
        // iOS Safari: only the video element itself can go fullscreen.
        video.webkitEnterFullscreen();
      }
    } catch (error) {
      console.error("[WalkthroughVideo] Fullscreen failed:", error);
    }
  }, []);

  return (
    <figure
      ref={figureRef}
      className={`group/video relative overflow-hidden rounded-2xl border border-gray-200 bg-gray-900 ${
        isFullscreen ? "flex items-center justify-center rounded-none border-0" : ""
      } ${className}`}
    >
      {playing ? (
        <>
          <video
            ref={videoRef}
            className={
              isFullscreen
                ? "block max-h-full max-w-full"
                : "block h-auto w-full"
            }
            src={src}
            poster={poster}
            controls
            autoPlay
            playsInline
            {...walkthroughVideoProps}
          />
          <SimpleTooltip content={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
            <button
              type="button"
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-gray-900/70 text-white opacity-0 transition hover:bg-gray-900/90 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/70 group-hover/video:opacity-100"
            >
              {isFullscreen ? (
                <Minimize2 className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Maximize2 className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </SimpleTooltip>
        </>
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={`Play the walkthrough: ${title}`}
          className="group relative block w-full"
        >
          <Image
            src={poster}
            alt={`Walkthrough: ${title}`}
            width={width}
            height={height}
            sizes="(max-width: 640px) 100vw, 900px"
            className="block h-auto w-full"
          />
          <span className="absolute inset-0 flex items-center justify-center bg-gray-900/20 transition group-hover:bg-gray-900/35">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-lg transition group-hover:scale-105">
              <Play
                className="ml-1 h-7 w-7 text-gray-900"
                fill="currentColor"
                aria-hidden="true"
              />
            </span>
          </span>
        </button>
      )}
    </figure>
  );
}
